import { useMemo } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { TakusoLastUpdated } from '~/components/balance/TakusoLastUpdated'
import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WeatherDataSource } from '~/components/common/WeatherDataSource'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useAreaWeatherActual } from '~/hooks/weather/useAreaWeatherActual'
import { useAreaWeatherIcons } from '~/hooks/weather/useAreaWeatherIcons'
import { convertAreaLabel } from '~/models/area'
import { hasAwaitingKoma } from '~/models/awaitingVoltage'
import { toProtoDate } from '~/models/dateTime'
import {
  buildKomaWeatherActual,
  hasAnyWeatherData,
} from '~/models/weather/actualKoma'
import { DetailChart } from '~/pages/BalanceDemandDetail/components/DetailChart'
import { FilterCard } from '~/pages/BalanceDemandDetail/components/FilterCard'
import { KomaTable } from '~/pages/BalanceDemandDetail/components/KomaTable'
import { SummaryBar } from '~/pages/BalanceDemandDetail/components/SummaryBar'
import { WeatherChart } from '~/pages/BalanceDemandDetail/components/WeatherChart'
import {
  convertDetailResponse,
  useDemandDetailWithBgSelection,
} from '~/pages/BalanceDemandDetail/hooks/useDemandDetail'
import { useDetailFilterState } from '~/pages/BalanceDemandDetail/hooks/useDetailFilterState'
import { routePaths } from '~/routes/routePaths'

export const BalanceDemandDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const areaCode = Number(
    searchParams.get('areaCode') ?? AreaCode.TOKYO,
  ) as AreaCode
  const areaName = convertAreaLabel(areaCode)

  const {
    displayDate,
    filter,
    changeDate,
    goToToday,
    changeFilter,
    correctFilter,
  } = useDetailFilterState(areaCode)

  // 天気アイコン帯 + 予想気温は気象予報 gRPC (WeatherForecastService) のライブ予報
  // から組み立てる (= 気象 gRPC 1 本を全画面で使い回す設計。ダッシュボードと同じ)。
  const { weatherIcons, weatherTemps, weatherTempValues } = useAreaWeatherIcons(
    {
      areaCode,
      displayDate,
    },
  )

  // 気温・露点・降水量の実績は実績気象 gRPC (WeatherActualService) から取得し、
  // 48 コマ気象チャートへ描画する (design bg-region の chart-tempdew-card)。
  const targetDate = toProtoDate(displayDate)
  const { data: weatherActualData, isError: weatherActualIsError } =
    useAreaWeatherActual({
      areaCode,
      targetDateFrom: targetDate,
      targetDateTo: targetDate,
    })
  // 予想気温 (点線) は予報 gRPC 由来の数値系列をコマ番号で実績系列に合流させる。
  const weatherActualSeries = useMemo(
    () =>
      buildKomaWeatherActual(
        weatherActualData?.actual?.points ?? [],
        weatherTempValues,
      ),
    [weatherActualData, weatherTempValues],
  )
  // 気象チャートの系列可視性: 分析補助/詳細の親チェックボックス OFF は配下の
  // 系列をまとめて無効化する (design bg-region の親子連動)。
  const weatherVisibility = {
    temperature: filter.analysisEnabled && filter.series.temperature,
    dewPoint: filter.detailEnabled && filter.series.dewPoint,
    precipitation: filter.analysisEnabled && filter.series.precipitation,
  }
  // 気象系列トグルが 1 つでも ON かつ実測値が存在すれば気象チャートを描画。
  const anyWeatherSeriesOn =
    weatherVisibility.temperature ||
    weatherVisibility.dewPoint ||
    weatherVisibility.precipitation
  // 「気象データなし」降格 (AC-034) は (1) 実績 RPC の取得失敗、(2) 実績・予報とも
  // 値が無い、の 2 経路。実績 RPC 失敗時は予報値が残っていても降格させる (実績の
  // 欠落を予想気温だけのチャートで隠さない)。正常応答で実測ゼロの未来日は
  // hasAnyWeatherData が予想気温を拾って表示継続する (実測ゼロと取得失敗の分離)。
  const weatherUnavailable =
    weatherActualIsError || !hasAnyWeatherData(weatherActualSeries)
  const {
    query: { data: rawData, isError },
    isRegionLoading,
    bgSelection,
  } = useDemandDetailWithBgSelection(
    displayDate,
    areaCode,
    filter,
    correctFilter,
  )
  // 電圧階級で絞り込んでいる間は計画・誤差を出さない。確定需要想定は BG 全体
  // しか存在しない (#2001) ため、階級別の実績と並べると誤差が実態と乖離する。
  // 需要チャートの系列可視性 (基本表示 + 親子連動を効かせた実効値)。
  const demandSeries = {
    demandPlan: filter.series.demandPlan && bgSelection.isDemandPlanEnabled,
    demandActual: filter.series.demandActual,
    weatherStrip: filter.series.weather,
    // 選択で無効にした時も filter.series は書き換えない (URL・保存値に載せず、
    // 「BG 全体」へ戻した時に利用者の ON/OFF で復元するため。AC-BDB-031)。
    procurementNet:
      filter.analysisEnabled &&
      filter.series.procurementNet &&
      bgSelection.isProcurementNetEnabled,
    actualCorrected:
      filter.detailEnabled && filter.series.demandActualCorrected,
    voltageExtraHigh: filter.voltageEnabled && filter.series.voltageExtraHigh,
    voltageHigh: filter.voltageEnabled && filter.series.voltageHigh,
    voltageLow: filter.voltageEnabled && filter.series.voltageLow,
    temperature: weatherVisibility.temperature,
    precipitation: weatherVisibility.precipitation,
    dewPoint: weatherVisibility.dewPoint,
  }

  const fetched = rawData ? convertDetailResponse(rawData) : undefined

  /**
   * spec `doji-bg.md` AC-BDB-033 [P0]:「IF 指定エリアのデータが取得できない場合
   * THEN 画面上部にエラーバナー『エリアデータの取得に失敗しました』を表示し、
   * テーブル・チャートを空状態にする」。
   *
   * 以前は `isLoading || !detail` だけで分岐しており、 取得に失敗すると
   * `detail` が undefined のまま = **永久にローディング表示**だった (issue #7064)。
   * TanStack Query は失敗時に `isLoading = false` / `data = undefined` を返すので、
   * この式は error でも真になり続ける。 さらに 5 秒間隔の refetch があるため、
   * 失敗し続ける限りロゴが回り続ける。
   */
  const loadFailed = isError

  /**
   * 取得に失敗している間は**本体 (テーブル・チャート) を描かない**。
   *
   * [spec 差異] AC-BDB-033 の文言は「テーブル・チャートを**空状態にする**」 で、
   * 初版は列見出しだけの空テーブルと空チャートを出していた。 2026-09-09 デザイナー
   * 判断で**非表示**にする —「alert の下にある blank の部品は非表示で良さそう」。
   * 空の器が並ぶと、 バナーを読んだ後に**まだ何か出るのを待つ**画面に見える。
   *
   * AC の意図 (古い値を残さない) は満たしたままで、 見せ方だけを変えている。
   * 初回か背景 refetch かは問わない (AC は初回に限定していない。 古い表を
   * バナーの下に残すと**いつの値か分からないまま読ませる**ため。 2026-09-09
   * レビュー指摘)。 spec 側の追随は #7064 で扱う。
   *
   * 同時同量の一覧 (AC-BDB-013 / AC-BDH-013) は「バナーを表示する」 だけを
   * 要求しており、 そちらは表示済みカードを残す。 AC の要求が違うので揃えない。
   */
  const detail = loadFailed ? undefined : fetched

  /**
   * `SummaryBar` にだけ「未取得」 を **null** で伝える。
   *
   * 取得できていない時に 0 を渡すと「次のGC コマ 1 ・ 00:00〜00:30」 と
   * **確定値の見た目で**描かれる (2026-09-09 レビュー指摘)。 チャート
   * (`DetailChart` / `WeatherChart`) は同じ 0 を受けても、 データが空で x 軸の
   * ドメインが無く GC 縦線を描けない (Recharts の `ifOverflow: 'discard'`) ので
   * 区別が要るのは**数値を文字で出す SummaryBar だけ**。
   */
  const gcKomaForSummary = loadFailed ? null : detail?.gcKoma

  const breadcrumbItems = [
    {
      label: t('common:nav.balance'),
      to: routePaths.balanceDemand.path,
    },
    {
      label: t(routePaths.balanceDemand.name),
      to: routePaths.balanceDemand.path,
    },
    {
      label: areaName,
      bold: true,
    },
  ]

  return (
    <WorkflowPageLayout
      /* 最終更新・気象データ出典を page-header の右端へ (2026-09-09 デザイナー判断)。
         [design 差異] 8080 は `balance/bg-region.html:145-150` の `.region-meta` 行として
         フィルタ直下に置いているが、 **同時同量ダッシュボード (`BalanceDemand`) は既に
         同じ情報を page-header 右端に出している**。 詳細画面だけ本文側にあると、 同じ
         情報の置き場所が画面で変わる。 縦も 1 行ぶん空く。 design 側の追随は #7064。 */
      breadcrumbItems={breadcrumbItems}
      headerExtra={
        <Flex alignItems="center" gap={2}>
          <WeatherDataSource />
          <Text textStyle="caption" color="Text.midEmphasis" opacity={0.6}>
            {t('common:metaSeparator')}
          </Text>
          <TakusoLastUpdated
            label={t('page:balanceDemandDetail.takusoLastUpdated')}
            lastUpdatedAt={rawData?.takusoLastUpdatedAt}
          />
        </Flex>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <FilterCard
          bgFilter={bgSelection}
          displayDate={displayDate}
          filter={filter}
          hasAwaitingVoltageKoma={
            detail !== undefined && hasAwaitingKoma(detail.komas)
          }
          onDateChange={changeDate}
          onFilterChange={changeFilter}
          onToday={goToToday}
        />

        {/* AC-BDB-033: 取得失敗は画面上部のバナーで伝える。 背景 refetch の失敗でも
            出す (AC は初回取得に限定していない)。

            上の余白 16px は 2026-09-09 デザイナー指定。 直上のフィルタカードと
            くっついていると、 バナーがカードの一部に見えて独立した警告として
            読めないため。 */}
        {isError && (
          <Box marginTop="16px" marginBottom="4px">
            <CompactAlert
              status="error"
              title={t('page:balanceDemandDetail.loadError')}
            />
          </Box>
        )}

        {/* 取得失敗中は **ローディングも本体も出さない** (バナーだけ)。
            `detail` を undefined にして本体を隠すと、 この分岐が
            `!detail` で真になり**ローディングロゴが復活する** — この PR が
            直した当の「永久にローディング」 と同じ絵になる (実測で踏んだ)。
            失敗の判定を先に置いて両方を抑止する。 */}
        {loadFailed ? null : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            display="flex"
            minHeight={!detail ? '200px' : 0}
            // 初回の取得中と、 表示日・絞り込みを変えた後に前の結果を残して取り直している間だけ
            // 覆う (UI 辞書 components/region-loading.md)。 5 秒ごとの自動更新では覆わない。
            isLoading={isRegionLoading}
          >
            {!detail ? null : (
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                {/* 左ペイン (コマテーブル)。 design `css/balance.css:288` の
                `.region-left { width: 380px }` 準拠。
                8080 は 2026-06-23 に 280px から広げており、 同 :285-287 に
                「280px では 4列(時刻/計画値/実績値+補完バッジ/誤差%) + 縦スクロールバー
                gutter が収まらず横スクロールが出て**誤差%列が欠けていた**」と経緯がある。
                実装は 280px のままで同じ欠けが起きていた。 */}
                <div
                  style={{
                    width: '380px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <KomaTable
                    currentKoma={detail.currentKoma}
                    data={detail.komas}
                  />
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    // 縦が足りない時はチャートを潰さずこのペインをスクロールさせる
                    // (design balance.css `.region-right`)。hidden のままチャート側に
                    // 高さの床を置くと、はみ出した X 軸が見えないまま切り落とされる。
                    overflowY: 'auto',
                  }}
                >
                  <SummaryBar
                    displayDate={displayDate}
                    frontier={
                      loadFailed ? undefined : detail?.gateCloseFrontier
                    }
                    gcKoma={gcKomaForSummary ?? null}
                    summary={detail.summary}
                    weatherActual={weatherActualSeries}
                  />
                  <DetailChart
                    currentKoma={detail.currentKoma}
                    data={detail.komas}
                    displayDate={displayDate}
                    gcKoma={detail.gcKoma}
                    seriesVisibility={demandSeries}
                    /* 空状態の `gcKoma: 0` は実値ではないので GC 系マーカーを描かない
                   (SummaryBar 側の `gcKomaForSummary` と同じ理由)。 */
                    showGcMarkers={!loadFailed}
                    weatherActual={weatherActualSeries}
                    weatherIcons={weatherIcons}
                    weatherTemps={weatherTemps}
                  />
                  {anyWeatherSeriesOn && (
                    <WeatherChart
                      visibility={weatherVisibility}
                      currentKoma={detail.currentKoma}
                      displayDate={displayDate}
                      gcKoma={detail.gcKoma}
                      series={weatherActualSeries}
                      showGcMarkers={!loadFailed}
                      unavailable={weatherUnavailable}
                    />
                  )}
                </div>
              </div>
            )}
          </RegionLoading>
        )}
      </div>
    </WorkflowPageLayout>
  )
}
